import { BASE_AUTH_OFFICE_API } from '@/core/api-config';
import { TokenManager } from '@/core/auth/token-manager';
import { ApiErrorMiddleware } from '@/core/api-error-middleware';
import { useOperatorStore } from '@/core/stores/operator-store';
import { appConfig } from '@/core/config';
import {
  AssetListParams,
  AssetListResponse,
  AssetDetailResponse,
  AssetUploadFormData,
  UploadAssetRequest,
  UploadAssetResponse,
  DeleteAssetResponse,
  RollbackAssetResponse,
} from '../types/asset-types';

/**
 * Asset Management API
 * Handles all API calls for managing generic assets (icons, images, videos, Lottie animations)
 */
export class AssetManagementApi {
  constructor(
    private readonly baseUrl: string = BASE_AUTH_OFFICE_API,
    private readonly tokenManager: TokenManager
  ) {}

  private async getAuthHeadersWithOperator(): Promise<Record<string, string>> {
    const operatorId = useOperatorStore.getState().selectedOperator?.operatorId;

    if (!operatorId) {
      console.error('AssetManagementApi: No operator selected, preventing API call');
      throw new Error('No operator selected. Please select an operator first.');
    }

    const headers = await this.tokenManager.getAuthHeaders();

    return {
      'Content-Type': 'application/json',
      ...headers,
      'X-Operator-Id': operatorId.toString(),
    };
  }

  /**
   * Get assets list with filtering and pagination
   * API: GET /api/v1/assets?applicationType={number}&assetType={number}&search={string}&skip={number}&take={number}
   */
  async getAssets(params: AssetListParams): Promise<AssetListResponse> {
    const headers = await this.getAuthHeadersWithOperator();

    // Build query string
    const queryParts: string[] = [];

    if (params.applicationType !== undefined) {
      queryParts.push(`applicationType=${params.applicationType}`);
    }

    if (params.assetType !== undefined) {
      queryParts.push(`assetType=${params.assetType}`);
    }

    if (params.operatorId !== undefined) {
      queryParts.push(`operatorId=${params.operatorId}`);
    }

    if (params.machineId !== undefined) {
      queryParts.push(`machineId=${params.machineId}`);
    }

    if (params.search) {
      queryParts.push(`search=${encodeURIComponent(params.search)}`);
    }

    if (params.skip !== undefined) {
      queryParts.push(`skip=${params.skip}`);
    }

    if (params.take !== undefined) {
      queryParts.push(`take=${params.take}`);
    }

    const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
    const url = `${this.baseUrl}/assets${queryString}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers,
      },
      'Asset Management API',
      '/assets'
    );

    const data = await response.json();

    // Handle array format
    if (Array.isArray(data)) {
      return {
        isSuccess: true,
        items: data,
        totalCount: data.length,
      };
    }

    // Normalize PascalCase or camelCase response
    const items = data.items ?? data.Items ?? [];
    const isSuccess = data.isSuccess ?? data.IsSuccess ?? true;
    const totalCount = data.totalCount ?? data.TotalCount ?? items.length;
    const errorMessage = data.errorMessage ?? data.ErrorMessage;

    return {
      isSuccess,
      items,
      totalCount,
      errorMessage,
    };
  }

  /**
   * Get asset detail with version history
   * API: GET /api/v1/assets/{assetId}
   */
  async getAssetDetail(assetId: number): Promise<AssetDetailResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/assets/${assetId}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers,
      },
      'Asset Management API',
      `/assets/${assetId}`
    );

    const data = await response.json();

    // Normalize PascalCase or camelCase response
    const asset = data.asset ?? data.Asset;
    const versions = data.versions ?? data.Versions ?? [];
    const isSuccess = data.isSuccess ?? data.IsSuccess ?? (asset != null);
    const errorMessage = data.errorMessage ?? data.ErrorMessage;

    return {
      isSuccess,
      asset,
      versions,
      errorMessage,
    };
  }

  /**
   * Upload a new asset or new version
   * API: POST /api/v1/assets
   *
   * @param request - Upload form data. If operatorId is provided (distributor users),
   *                  it will be used. Otherwise, the current operator from context is used.
   */
  async uploadAsset(request: AssetUploadFormData): Promise<UploadAssetResponse> {
    // Use provided operatorId (for distributors) or fall back to current operator
    const operatorId = request.operatorId ?? useOperatorStore.getState().selectedOperator?.operatorId;

    if (!operatorId) {
      throw new Error('No operator selected. Please select an operator first.');
    }

    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/assets`;

    // Build the final request with operatorId and optional machineId
    const env = appConfig.getRuntimeConfig()?.environment ?? appConfig.getEnvironment();
    const requestWithOperator: UploadAssetRequest = {
      applicationType: request.applicationType,
      assetType: request.assetType,
      operatorId,
      machineId: request.machineId,
      name: request.name,
      description: request.description,
      fileName: request.fileName,
      fileContent: request.fileContent,
      contentType: request.contentType,
      environment: env === 'prod' ? 2 : 1,
      screenSize: request.screenSize,
    };

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(requestWithOperator),
      },
      'Asset Management API',
      '/assets'
    );

    const result = await response.json();

    // Check for API-level validation errors
    if (result.isSuccess === false) {
      throw new Error(result.errorMessage || 'Failed to upload asset');
    }

    return result;
  }

  /**
   * Delete an asset
   * API: DELETE /api/v1/assets/{assetId}
   */
  async deleteAsset(assetId: number): Promise<DeleteAssetResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/assets/${assetId}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'DELETE',
        headers,
      },
      'Asset Management API',
      `/assets/${assetId}`
    );

    const result = await response.json();
    return result;
  }

  /**
   * Rollback to a previous version
   * API: POST /api/v1/assets/{assetId}/rollback/{versionId}
   */
  async rollbackAsset(assetId: number, versionId: number): Promise<RollbackAssetResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/assets/${assetId}/rollback/${versionId}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'POST',
        headers,
      },
      'Asset Management API',
      `/assets/${assetId}/rollback/${versionId}`
    );

    const result = await response.json();

    // Check for API-level validation errors
    if (result.isSuccess === false) {
      throw new Error(result.errorMessage || 'Failed to rollback asset');
    }

    return result;
  }
}
